import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogIsCatalogOnly, getCatalogIsTimedPlus } from '@/redux/modules/catalog/catalogs/catalog.selectors';

import useGetCatalogStatus, { CatalogStatus as CatalogStatusEnum } from '../../hooks/useGetCatalogStatus';
import { CatalogOnlyAuctionStatus } from './components/CatalogOnlyAuctionStatus';
import TimedOrLiveAuctionStatus from './components/TimedOrLiveAuctionStatus';
import TimedOrLiveAuctionStatusMobile from './components/TimedOrLiveAuctionStatusMobile';
import { TimedPlusAuctionStatus } from './components/TimedPlusAuctionStatus';
import { TimedPlusAuctionStatusMobile } from './components/TimedPlusAuctionStatusMobile';

type Props = {
    catalogId: number;
    forceIsLive: boolean;
    layout?: 'sidebar' | 'inline';
    setForceIsLive: (force: boolean) => void;
};

// no direct hammer-ui colors for the old caterwaul colors so here we are.
const statusBackgroundColor: Record<CatalogStatusEnum, string> = {
    [CatalogStatusEnum.Approved]: 'bg-[#e5f4f8]',
    [CatalogStatusEnum.Declined]: 'bg-[#fff6f6] min-h-[360px]',
    [CatalogStatusEnum.Live]: 'bg-[#fbf6f4]',
    [CatalogStatusEnum.Pending]: 'bg-[#f4f4f4] min-h-[360px]',
    [CatalogStatusEnum.PendingCatalogOnly]: 'bg-[#f4f4f4]',
};

const CatalogStatus = ({ catalogId, forceIsLive, layout = 'sidebar', setForceIsLive }: Props) => {
    const isCatalogOnly = useAppSelector((state) => getCatalogIsCatalogOnly(state, catalogId));
    const isTimedPlus = useAppSelector((state) => getCatalogIsTimedPlus(state, catalogId));
    const currentCatalogStatus = useGetCatalogStatus(catalogId, forceIsLive);

    return (
        <div
            className={
                layout === 'inline'
                    ? 'w-full'
                    : cn(
                          'h-full min-w-[290px] border-t-0 px-md py-lg',
                          statusBackgroundColor[currentCatalogStatus] ?? 'bg-[#e5f4f8]'
                      )
            }
            data-testid="catalogStatus"
            suppressHydrationWarning
        >
            {isCatalogOnly ? (
                <CatalogOnlyAuctionStatus
                    catalogId={catalogId}
                    layout={layout}
                />
            ) : isTimedPlus ? (
                layout === 'inline' ? (
                    <TimedPlusAuctionStatusMobile
                        catalogId={catalogId}
                        setForceIsLive={setForceIsLive}
                    />
                ) : (
                    <TimedPlusAuctionStatus
                        catalogId={catalogId}
                        setForceIsLive={setForceIsLive}
                    />
                )
            ) : layout === 'inline' ? (
                <TimedOrLiveAuctionStatusMobile
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                    setForceIsLive={setForceIsLive}
                />
            ) : (
                <TimedOrLiveAuctionStatus
                    catalogId={catalogId}
                    forceIsLive={forceIsLive}
                    setForceIsLive={setForceIsLive}
                />
            )}
        </div>
    );
};

export default CatalogStatus;
